import * as XLSX from "xlsx";
import { StorageConstant } from "../constants/Constants";
import { getFromStorage } from "./Helper";

/** Toast copy when brand/qty (or no valid row) fails client-side bulk Excel validation. */
const BULK_EXCEL_INVALID_DATA_EN =
  "Incorrect information provided in the Excel sheet.";
const BULK_EXCEL_INVALID_DATA_ES =
  "Información incorrecta proporcionada en la hoja de Excel.";
const BULK_EXCEL_ENTER_DATA_LEGACY_EN = "Please enter data in your Excel sheet.";
const BULK_EXCEL_ENTER_DATA_LEGACY_ES =
  "Por favor ingrese los datos en su hoja de Excel.";

export function getBulkExcelInvalidDataToastMessage(
  languageConstantsValue?: string,
): string {
  if (
    languageConstantsValue &&
    languageConstantsValue !== BULK_EXCEL_ENTER_DATA_LEGACY_EN &&
    languageConstantsValue !== BULK_EXCEL_ENTER_DATA_LEGACY_ES
  ) {
    return languageConstantsValue;
  }
  const translate = getFromStorage({ key: StorageConstant?.Translate });
  return translate === "spa" ? BULK_EXCEL_INVALID_DATA_ES : BULK_EXCEL_INVALID_DATA_EN;
}

/** Row 1 base columns — must match API `create_excel` `baseHeaders`. Optional 8th column header: `SKU`. */
const HEADER_BASE = [
  "ITEM",
  "BRAND*",
  "PART NUMBER*",
  "QTY*",
  "DESCRIPTION*",
  "SERIAL NUMBER",
  "MODEL NUMBER",
];

const QTY_REGEX = /^[0-9]+$/;

function cellStr(v: unknown): string {
  return String(v ?? "").trim();
}

/** Brand must be non-empty text, not digits-only (inverse of QTY rule). */
function isValidBrand(brand: string): boolean {
  return brand !== "" && !QTY_REGEX.test(brand);
}

function isValidQty(qty: string): boolean {
  return qty !== "" && QTY_REGEX.test(qty);
}

/**
 * Validates bulk-quote .xlsx before upload (aligned with server rules for empty vs header).
 * @returns `null` if at least one valid data row exists; otherwise a reason code.
 */
export function validateBulkQuoteExcelBuffer(buf: ArrayBuffer): "empty" | "invalid_header" | null {
  let wb: XLSX.WorkBook;
  try {
    wb = XLSX.read(buf, { type: "array", cellDates: false });
  } catch {
    return "invalid_header";
  }
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return "empty";
  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<(string | number | undefined)[]>(sheet, {
    header: 1,
    defval: "",
    raw: false,
  }) as (string | number | undefined)[][];

  if (!rows?.length) return "empty";

  const headerRow = (rows[0] || []).map(cellStr);
  const baseOk = HEADER_BASE.every((exp, i) => headerRow[i] === exp);
  if (!baseOk) return "invalid_header";

  const skuHeader = headerRow.length > 7 ? cellStr(headerRow[7]) : "";
  if (skuHeader !== "" && skuHeader.toUpperCase() !== "SKU") {
    return "invalid_header";
  }

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const hasAny = row.some((c) => cellStr(c) !== "");
    if (!hasAny) continue;
    const brand = cellStr(row[1]);
    const part = cellStr(row[2]);
    const qty = cellStr(row[3]);
    if (isValidBrand(brand) && part && isValidQty(qty)) return null;
  }

  return "empty";
}
